import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BrandingConfigService } from '../services/org-config/org-config.service';
import { ThemeService } from '../services/theme/theme.service';
import { hexToHslString } from '../utils/color-conversion';

/**
 * APP_INITIALIZER factory function to load and apply theme from API.
 * 
 * This initializer:
 * 1. Fetches branding configuration from the public API
 * 2. Converts hex colors to HSL format
 * 3. Applies colors as CSS variables
 * 4. Sets the theme mode (light/dark)
 * 5. Handles errors gracefully with fallback defaults
 * 6. Blocks application bootstrapping until all dynamic routes have loaded
 * 
 * Runs before the Angular application is fully bootstrapped, ensuring
 * theme is applied before UI renders (prevents flickering).
 * 
 * @returns Promise that resolves when theme initialization is complete
 */
export async function initializeThemeFactory(): Promise<void> {
  const brandingConfigService = inject(BrandingConfigService);
  const themeService = inject(ThemeService);

  try {
    // Fetch branding configuration from API
    const config = await firstValueFrom(brandingConfigService.loadBrandingConfig()) as ReturnType<typeof brandingConfigService.loadBrandingConfig> extends any[] ? never : unknown;

    if (!config || typeof config !== 'object') {
      console.warn('No branding configuration loaded');
      return;
    }

    // The config returned from loadBrandingConfig is BrandingConfig directly (not wrapped in 'data')
    const brandingConfig = config as Record<string, any>;

    // Set favicon if available
    if (brandingConfig['favicon']) {
      const faviconElement = document.getElementById('app-favicon') as HTMLLinkElement;
      if (faviconElement) {
        faviconElement.href = brandingConfig['favicon'];
      }
    }

    const themeSetting = brandingConfig['theme_setting'];

    // Validate that themeSetting exists
    if (!themeSetting || typeof themeSetting !== 'object') {
      console.warn('No theme_setting found in config. Available keys:', Object.keys(brandingConfig));
      console.warn('Full config object:', brandingConfig);
      return;
    }

    // Convert hex colors to HSL format
    try {
      
      // Validate required fields exist
      if (!themeSetting['primaryColor']) {
        console.warn('primaryColor not found in theme_setting');
        return;
      }

      // Get the secondary color - could be 'secondaryColor' or 'accentColor'
      const secondaryColor = themeSetting['secondaryColor'] || themeSetting['accentColor'];
      if (!secondaryColor) {
        console.warn('secondaryColor/accentColor not found in theme_setting');
        return;
      }

      const primaryHsl = hexToHslString(themeSetting['primaryColor']);
      const accentHsl = hexToHslString(secondaryColor);

      // Apply both colors at once
      themeService.setTokens({
        primary: primaryHsl,
        secondary: accentHsl,
      });
    } catch (colorError) {
      console.error('Failed to convert colors:', colorError);
    }

    // Set the theme mode
    themeService.setMode(themeSetting['mode']);

  } catch (error) {
    console.error('Theme initialization failed, using defaults:', error);
    // ThemeService defaults will be applied
  }
}
